import type { JsonSchemaNode } from "@nmblr-platform-flow/schema";
import type { ComponentType } from "react";
import { humanize } from "../../../lib/humanize.js";
import type { TemplateVariable } from "../../../lib/schema-paths.js";

interface SchemaFieldProps {
	name: string;
	schema: JsonSchemaNode;
	value: unknown;
	onChange: (value: unknown) => void;
	required?: boolean;
	templateVariables?: TemplateVariable[];
}

interface Props {
	name: string;
	schema: JsonSchemaNode;
	value: unknown;
	onChange: (value: unknown) => void;
	required?: boolean;
	SchemaField: ComponentType<SchemaFieldProps>;
	templateVariables?: TemplateVariable[];
}

export function ObjectFields({
	name,
	schema,
	value,
	onChange,
	required,
	SchemaField,
	templateVariables,
}: Props) {
	const obj = (typeof value === "object" && value !== null ? value : {}) as Record<string, unknown>;
	const properties = schema.properties ?? {};
	const requiredFields = schema.required ?? [];

	if (Object.keys(properties).length === 0) return null;

	return (
		<fieldset className="mb-3 rounded-xl border border-border p-3">
			<legend className="px-1 text-xs text-text-muted">
				{humanize(name)}
				{required && <span className="ml-1 text-error">*</span>}
			</legend>
			{schema.description && <p className="mb-2 text-xs text-text-muted">{schema.description}</p>}
			{Object.entries(properties).map(([key, propSchema]) => (
				<SchemaField
					key={key}
					name={key}
					schema={propSchema}
					value={obj[key]}
					onChange={(v) => onChange({ ...obj, [key]: v })}
					required={requiredFields.includes(key)}
					templateVariables={templateVariables}
				/>
			))}
		</fieldset>
	);
}
